import { cn } from "@tcg/ui";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

export type ToastTone = "info" | "success" | "error";

interface ToastItem {
  id: number;
  text: string;
  tone: ToastTone;
}

interface ToastApi {
  show(text: string, tone?: ToastTone): void;
}

const ToastContext = createContext<ToastApi>({ show: () => undefined });

/** Short messages in plain words (errors from the envelope, "added", "sent") — 4 s, top right. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const next = useRef(1);
  const show = useCallback((text: string, tone: ToastTone = "info") => {
    const id = next.current;
    next.current += 1;
    setItems((list) => [...list.slice(-2), { id, text, tone }]);
    setTimeout(() => setItems((list) => list.filter((i) => i.id !== id)), 4_000);
  }, []);
  const api = useMemo(() => ({ show }), [show]);
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="assertive"
        className="pointer-events-none fixed right-4 top-16 z-50 flex w-96 flex-col gap-2"
      >
        {items.map((item) => (
          <div
            key={item.id}
            role={item.tone === "error" ? "alert" : "status"}
            className={cn(
              "pointer-events-auto rounded-md border px-4 py-3 text-base shadow-lg",
              item.tone === "error" && "border-status-problem bg-status-problem text-white",
              item.tone === "success" && "border-pos-ok bg-pos-ok text-white",
              item.tone === "info" && "bg-background",
            )}
          >
            {item.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  return useContext(ToastContext);
}
